import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Alert } from 'rsuite';
import { TablePaginationProps } from 'rsuite/lib/Table/TablePagination';
import { AlertWithTimestamp } from '../../../../components/AlertWithTimestamp';
import { Exception } from '../../../../data';
import { extractApiErrorMessage } from '../../../../utils/extractApiErrorMessage';
import IpaMath from '../../../../lib/IPAMaths';
import { FiltrosModelProps, checkIfIsLoading, getFiltrosModel } from '../../../../utils';
import { getSetupIpaBasicConfig } from '../../../Administrador/Produtos/Components/SetupIpaBasicConfig/service';
import { NegotiationDataItemProps, NegotiationDataProps, NegotitationRowDataProps, SuppliersResponseContentProps, SuppliersResponseProps } from '../pages';
import { PATCHNegotiationDetailsSaveEditedDatapoint, desfazerAlteracaoDePrecos } from '../pages/DetalhesDaNegociacao/DetalhesDaNegociacao.services';
import { ITableFornecedor } from '../pages/NegociacaoFornecedor/components';
import {
  getCurrentInputCostForNegotiation,
  normalizeCostNegotiationMode,
  resolveLiquidCostForNegotiation,
  resolveWorkingLiquidCostForNegotiation,
} from '../pages/NegociacaoFornecedor/costNegotiation';
import { competitorLoadingArea } from '../pages/NegociacaoFornecedor/NegociacaoFornecedor.rowIdentity';
import { getNetworkCompetitorsInfo } from '../pages/NegociacaoFornecedor/NegociacaoFornecedor.networkCompetitors';
import { getCompetitorsInfoSuppliers } from '../pages/NegociacaoFornecedor/NegociacaoFornecedor.services';
import {
  PMZNewCostCalc,
  getEditedDatapointModel,
  idealPurchasePriceOf,
  newPriceCompetitorsCalc,
} from '../pages/NegociacaoFornecedor/utils';
import { aplicarDerivadosDaGravacao, numerosQueATelaPodeAdiantar } from '../pages/NegociacaoFornecedor/derivadosDaGravacao';
import { linhaTemVerbaEmJogo } from '../pages/NegociacaoFornecedor/verbaDeSellIn';
import { edicoesDaMassaAplicada, linhasDaMassaAplicada } from '../pages/NegociacaoFornecedor/verbaEmMassa';
import type { ResultadoDaMassa } from '../pages/NegociacaoFornecedor/verbaEmMassa';
import { useVerbaDeSellIn } from '../pages/NegociacaoFornecedor/verbaDeSellIn.hooks';
import type { VerbaParaGravar } from '../pages/NegociacaoFornecedor/verbaDeSellIn.hooks';
import { GetNegociacoesItemProps } from '../pages/NegociacoesSalvas/NegociacoesSalvas.services';
import { VisaoGravada } from '../pages/DetalhesDaNegociacao/visaoDaNegociacaoSalva';
import { notifyItemSaveFailure } from './notifyItemSaveFailure';
import { usePagination } from './usePagination';
import { useSelectedDatapoints } from './useSelectedDatapoints';
import { useSort } from './useSort';

type UseNegotiationTableProps = {
  queryFn?: (data: FiltrosModelProps) => Promise<SuppliersResponseProps>;
  rowKey: keyof NegotitationRowDataProps;
  filters?: Partial<Record<string, string[]>>;
  negotiationStatus?: GetNegociacoesItemProps['status'];
  negotiationId?: string;
  /** O grão em que a negociação foi gravada. Decide como o detalhe de concorrência é buscado. */
  visionSaved?: VisaoGravada;
};

type UseNegotiationTable = ITableFornecedor & {
  isLoading: boolean;
  pagination: TablePaginationProps;
  /** Fica aqui porque escrever na linha é assunto desta grade, e não de quem abre o modal. */
  adotarVerbaEmMassa: (resultado: ResultadoDaMassa) => void;
};

const INITIAL_DATA: NegotiationDataProps = {
  content: [],
  totalElements: 0,
  totalPages: 0,
};

export const useNegotiationTable = ({
  rowKey,
  queryFn,
  filters,
  negotiationStatus,
  negotiationId,
  visionSaved,
}: UseNegotiationTableProps): UseNegotiationTable => {
  const isNetworkNegotiation = visionSaved === 'NETWORK';
  const [data, setData] = useState<NegotiationDataProps>(INITIAL_DATA);

  const [totalElements, setTotalElements] = useState<number>(0);

  const [sort, { onSort }] = useSort({
    type: 'productId',
    orderBy: 'asc',
  });

  const pagination = usePagination({ total: totalElements });

  const selectedDatapoints = useSelectedDatapoints();

  const { data: supplierNegotiationConfig } = useQuery({
    queryKey: ['ipa.setup.basicConfig'],
    queryFn: getSetupIpaBasicConfig,
  });

  const costNegotiationMode = normalizeCostNegotiationMode(supplierNegotiationConfig?.negotiation_cost);

  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);

  const verba = useVerbaDeSellIn();

  const model = getFiltrosModel(
    {
      sort,
      pageSize: pagination.displayLength,
      pageNumber: pagination.activePage,
    },
    'camel',
  );

  const { fetchStatus } = useQuery({
    queryKey: ['get-negotiation-datapoints', filters, sort, pagination.activePage, pagination.displayLength],
    queryFn: () => queryFn?.(model),
    onSuccess: data => {
      if (!data) return;
      setTotalElements(data.totalElements);
      setData(oldValue => ({
        ...oldValue,
        content: data.content,
      }));
    },
    retry: false,
  });

  const updateCompetitorData = (productsToBePricedId: string, response: NegotiationDataItemProps['competitorsInfo']) => {
    setData(oldState => ({
      ...oldState,
      content: data.content.map(item => {
        if (item.productsToBePricedId === productsToBePricedId) {
          return {
            ...item,
            competitorsInfo: response,
          };
        }
        return item;
      }),
    }));
  };

  const getInfoCompetitors = (rowData: NegotitationRowDataProps) => {
    const { storeId, productId, productFamilyId, competitorsPrice, productsToBePricedId, productFamily, cluster } = rowData;

    if (competitorsPrice !== 0 || productFamilyId) {
      const hasPreviousData = data.content.find(item => item.productsToBePricedId === productsToBePricedId)?.competitorsInfo;

      if (hasPreviousData) return;

      // A área do indicador de carregamento é nomeada pela mesma função que a grade usa para
      // desenhá-la, e a partir do campo que nomeia a linha nesta tela. Enquanto ela era montada aqui
      // com o código da loja e o do produto, as duas pontas só casavam por coincidência.
      const loadingArea = competitorLoadingArea(String(rowData[rowKey] ?? ''));

      // O identificador da negociação só viaja quando ela está fechada, que é quando o servidor tem
      // uma fotografia da concorrência gravada para devolver. Numa negociação em andamento a tela quer
      // o mercado de hoje, e mandar o identificador custaria uma consulta a cada abertura de card.
      const negotiationIdWhenClosed = negotiationStatus === 'CLOSED' ? negotiationId : undefined;

      // A linha de rede não tem loja, e as fontes de mercado vivo respondem no grão de uma. Em
      // andamento o detalhe é buscado loja por loja e juntado no navegador, como na grade; na
      // fechada a fotografia já foi gravada junta, e o servidor a resolve pelo produto.
      const competitorsInfoRequest =
        isNetworkNegotiation && !negotiationIdWhenClosed
          ? getNetworkCompetitorsInfo({
              productId,
              productFamilyId: productFamily ? productFamilyId : null,
              loadingArea,
            })
          : getCompetitorsInfoSuppliers(
              {
                productId,
                storeId: isNetworkNegotiation ? undefined : storeId,
                productFamilyId: productFamily ? productFamilyId : null,
                clusterId: isNetworkNegotiation ? undefined : cluster?.clusterId,
                negotiationId: negotiationIdWhenClosed,
              },
              loadingArea,
            );

      competitorsInfoRequest
        .then(data => {
          if (data) updateCompetitorData(productsToBePricedId, data);
        })
        .catch(error => {
          const errorMessage = extractApiErrorMessage(error);
          AlertWithTimestamp({
            message: errorMessage ?? 'Não foi possível buscar a concorrência deste item.',
          });
        });
    }
  };

  const handleExpandedRow = (rowData: NegotitationRowDataProps) => {
    let isOpen = false;

    const nextExpandedRowKeys = [];

    expandedRowKeys?.forEach(key => {
      if (key === rowData[rowKey]) {
        isOpen = true;
      } else {
        nextExpandedRowKeys.push(key);
      }
    });

    if (!isOpen) {
      nextExpandedRowKeys.push(rowData[rowKey] as string);
      getInfoCompetitors(rowData);
    }

    setExpandedRowKeys(nextExpandedRowKeys);
  };

  const handleChangeNewCost = (index: number, value: string, rowData: SuppliersResponseContentProps) => {
    const { salesTaxPercentage, expensesPercent } = rowData;
    const cost = Number(value.replace(/\./g, '').replace(',', '.'));

    const liquidCost = resolveLiquidCostForNegotiation(rowData as NegotiationDataItemProps, cost, costNegotiationMode);
    const PMZNewCost = PMZNewCostCalc(rowData, liquidCost);
    const newPriceCompetitors = rowData.newPrice !== null ? newPriceCompetitorsCalc(rowData, rowData.newPrice) : null;

    const hasCompetitorsPrice = rowData.competitorsPrice !== null && rowData.competitorsPrice > 0;

    const newCost = cost !== null && cost > 0 ? liquidCost : (rowData.cost ?? null);

    const newPriceMargin = rowData.newPrice !== null ? IpaMath.novaMargem(rowData.newPrice, salesTaxPercentage, expensesPercent, newCost) : null;
    const newCompetitorPriceMargin = hasCompetitorsPrice ? IpaMath.novaMargem(rowData.competitorsPrice, salesTaxPercentage, expensesPercent, newCost) : null;

    // O custo liquido acima e CEGO a verba, e o que sai dele sairia mais ALTO que a verdade numa
    // linha com acordo. Com verba, a celula conserva o que tem e espera o motor.
    const temVerbaEmJogo = linhaTemVerbaEmJogo(rowData, verba.edicaoDaLinha(String(rowData[rowKey as keyof SuppliersResponseContentProps] ?? '')));

    const dataContent = data?.content;
    if (value === '0,00') {
      verba.descartarPorFaltaDeNovoCusto(String(rowData[rowKey as keyof SuppliersResponseContentProps] ?? ''));

      dataContent[index] = {
        ...dataContent[index],
        newCost: null,
        newPrice: null,
        newPriceMargin: null,
        newPriceCompetitors: null,
        marginCompetitorsPrice: newCompetitorPriceMargin,
      };
    } else {
      dataContent[index] = {
        ...dataContent[index],
        newCost: cost ? cost : null,
        // Adiantados SO onde o calculo da tela nao mente; o que nao vem daqui a celula conserva.
        ...numerosQueATelaPodeAdiantar(temVerbaEmJogo, {
          PMZNewCost,
          newPriceMargin: newPriceMargin || null,
          marginCompetitorsPrice: newCompetitorPriceMargin,
        }),
        newPriceCompetitors: hasCompetitorsPrice ? newPriceCompetitors : null,
      };
    }
    setData(oldState => ({ ...oldState, content: dataContent }));
  };

  const handleChangeNewPrice = (index: number, value: string, rowData: SuppliersResponseContentProps) => {
    const { salesTaxPercentage, expensesPercent } = rowData;

    const newPrice = Number(value.replace(/\./g, '').replace(',', '.'));
    const currentCost = getCurrentInputCostForNegotiation(rowData as NegotiationDataItemProps, costNegotiationMode);
    const newCost = resolveWorkingLiquidCostForNegotiation(rowData as NegotiationDataItemProps, costNegotiationMode);
    const PMZNewCost = PMZNewCostCalc(rowData, newCost);
    const newPriceCompetitors = newPriceCompetitorsCalc(rowData, newPrice);

    const hasCompetitorsPrice = rowData.competitorsPrice !== null && rowData.competitorsPrice > 0;

    const newPriceMargin = rowData.newPrice !== null ? IpaMath.novaMargem(newPrice, salesTaxPercentage, expensesPercent, newCost) : null;
    const newCompetitorPriceMargin = hasCompetitorsPrice ? IpaMath.novaMargem(rowData.competitorsPrice, salesTaxPercentage, expensesPercent, newCost) : null;

    const temVerbaEmJogo = linhaTemVerbaEmJogo(rowData, verba.edicaoDaLinha(String(rowData[rowKey as keyof SuppliersResponseContentProps] ?? '')));

    const dataContent = data?.content;

    if (value === '0,00') {
      dataContent[index] = {
        ...dataContent[index],
        newCost: null,
        newPrice: null,
        newPriceMargin: null,
        newPriceCompetitors: null,
        marginCompetitorsPrice: newCompetitorPriceMargin,
      };
      setData(oldState => ({ ...oldState, content: dataContent }));
    } else {
      dataContent[index] = {
        ...dataContent[index],
        newPrice,
        ...numerosQueATelaPodeAdiantar(temVerbaEmJogo, { PMZNewCost, newPriceMargin, marginCompetitorsPrice: newCompetitorPriceMargin }),
        newPriceCompetitors: hasCompetitorsPrice ? newPriceCompetitors : null,
      };
      setData(oldState => ({ ...oldState, content: dataContent }));
    }
  };
  const handleChangeObjectiveMargin = (index: number, value: string, rowData: SuppliersResponseContentProps) => {
    // Campo vazio é "sem edição"; zero digitado é margem editada para 0%, que vence o recuo.
    const newObjectiveMargin = value === '' ? null : Number(value.replace(/\./g, '').replace(',', '.'));
    const { bestIdealPrice, bestIdealPricePMZ } = idealPurchasePriceOf({ ...rowData, newObjectiveMargin }, costNegotiationMode);

    const dataContent = data?.content;
    dataContent[index] = {
      ...dataContent[index],
      newObjectiveMargin,
      bestIdealPrice,
      bestIdealPricePMZ,
    };
    setData(oldState => ({ ...oldState, content: dataContent }));
  };

  const findDataAndUpdate = async (id: string, payload: Partial<NegotiationDataItemProps>) => {
    return new Promise<NegotiationDataItemProps>((resolve, reject) => {
      const index = data.content.findIndex(({ productsToBePricedId }) => productsToBePricedId === id);

      if (index === -1) {
        reject(new Error('Index not found'));
        return;
      }

      const currentData = data.content;

      currentData[index] = {
        ...currentData[index],
        ...payload,
      };

      setData(oldState => ({ ...oldState, content: currentData }));

      resolve(data.content[index]);
    });
  };

  /**
   * Cada item aplicado escreve a célula E a LINHA: só a célula deixa a dica do novo custo sem a
   * cadeia da verba. A grade NÃO é recarregada.
   */
  const adotarVerbaEmMassa = (resultado: ResultadoDaMassa) => {
    const patchesPorLinha = linhasDaMassaAplicada(resultado);

    if (Object.keys(patchesPorLinha).length === 0) return;

    setData(anterior => ({
      ...anterior,
      content: anterior.content.map(linha => {
        const patch = patchesPorLinha[String((linha as Record<string, unknown>)[rowKey as string] ?? '')];

        return patch ? { ...linha, ...patch } : linha;
      }),
    }));

    verba.adotarEdicoes(edicoesDaMassaAplicada(resultado));
  };

  const handleInputBlur = async (
    rowData: NegotitationRowDataProps,
    _index: number,
    details: {
      onBlurValue: number | null;
      onFocusValue: number | null;
      hasChanges: boolean;
    },
    verbaSobrescrita?: VerbaParaGravar | null,
  ) => {
    if (!details?.hasChanges) return;

    try {
      // Verba já gravada NÃO viaja de novo: a escrita no motor é parcial por presença de chave, e
      // reenviar a do líquido sem o valor ao fornecedor apaga a âncora que o comprador digitou.
      const model = getEditedDatapointModel(rowData, verbaSobrescrita ?? verba.verbaPendenteDeGravacao(String(rowData[rowKey] ?? '')));

      const res = await PATCHNegotiationDetailsSaveEditedDatapoint(model);

      // A resposta traz o indicador recalculado sobre o custo que acabou de ser gravado. Escrever
      // so a marca de editada descarta esse valor, e a celula fica com o indicador de antes.
      //
      // Chave ausente na resposta nao entra no patch, e a celula conserva o que ja tem.
      const patch = aplicarDerivadosDaGravacao(res);

      await findDataAndUpdate(rowData.productsToBePricedId, {
        isEdited: res.isEdited,
        costIndicator: res.costIndicator,
        ...patch,
      });

      // O MESMO patch vai para a edição local: em verba sugerida o motor a recalcula, e linha e
      // edição desencontradas deixam o campo, os pontos e o gap no valor de antes.
      verba.marcarComoGravada(String(rowData[rowKey] ?? ''), patch);
    } catch (error) {
      await notifyItemSaveFailure(error);
    }
  };

  /** Sair do campo le a verba da edicao pendente; aplicar a ideal a PASSA, no mesmo clique. */
  const handleVerbaBlur = async (rowData: NegotitationRowDataProps, verbaSobrescrita?: VerbaParaGravar | null) => {
    const verbaParaGravar = verbaSobrescrita ?? verba.verbaPendenteDeGravacao(String(rowData[rowKey] ?? ''));

    if (!verbaParaGravar) return;

    await handleInputBlur(rowData, 0, { onBlurValue: null, onFocusValue: null, hasChanges: true }, verbaParaGravar);
  };

  const handleDesfazerAlteracaoDePrecos = async (rowData: NegotitationRowDataProps) => {
    try {
      const res = await desfazerAlteracaoDePrecos(rowData);
      if (!res) return;
      await findDataAndUpdate(rowData.productsToBePricedId, res);
      Alert.success('Alteração de preço desfeita com sucesso');
    } catch {
      const { message } = new Exception('Erro ao desfazer alteração de preço');
      AlertWithTimestamp({ message });
    }
  };

  return {
    data: data.content,
    isLoading: checkIfIsLoading(fetchStatus),
    sortColumn: sort.type,
    onSortColumn: onSort,
    pagination,
    rowKey,
    selectedDatapoints,
    expandedRowKeys,
    offersNetworkCompetitionDetail: isNetworkNegotiation,
    onExpandRow: handleExpandedRow,
    onChangeNewCost: handleChangeNewCost,
    onChangeNewPrice: handleChangeNewPrice,
    onChangeObjectiveMargin: handleChangeObjectiveMargin,
    onBlur: handleInputBlur,
    verba,
    onVerbaBlur: handleVerbaBlur,
    adotarVerbaEmMassa,
    costNegotiationMode,
    totalElements,
    ...(negotiationStatus === 'ONGOING' && {
      contextMenu: {
        onUndoChanges: handleDesfazerAlteracaoDePrecos,
      },
    }),
  };
};
